import React from 'react';
import { Header } from '../../components/Header';
import { Footer } from '../../components/Footer';
import { LocaleProvider, useLocale } from '../../i18n/LocaleContext';
import { useSeo } from '../../seo/useSeo';
import { breadcrumbJsonLd } from '../../seo/breadcrumbs';
import '../../styles/landing.css';
import '../../styles/landing-neon.css';

const CONTACT = {
  email: 'gather.community.org@gmail.com',
};

const EFFECTIVE_DATE = 'May 5, 2026';

const TOC = [
  { id: 'real', label: 'Be real' },
  { id: 'welcoming', label: 'Be welcoming' },
  { id: 'safety', label: 'Safety first' },
  { id: 'harassment', label: 'No harassment' },
  { id: 'honest', label: 'Honest events' },
  { id: 'show-up', label: 'Show up or cancel' },
  { id: 'privacy', label: 'Privacy of others' },
  { id: 'spam', label: 'No spam, no scams' },
  { id: 'alcohol', label: 'Drugs & alcohol' },
  { id: 'enforcement', label: 'How we enforce' },
  { id: 'report', label: 'Report something' },
];

const PillarIcon = ({ children }: { children: React.ReactNode }): React.JSX.Element => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {children}
  </svg>
);

const PILLARS: { icon: React.ReactNode; title: string; body: string }[] = [
  {
    icon: (
      <PillarIcon>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
      </PillarIcon>
    ),
    title: 'Real people, real events',
    body: 'No fake profiles. No bait-and-switch.',
  },
  {
    icon: (
      <PillarIcon>
        <path d="M21 15a4 4 0 0 1-4 4H7l-4 4V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
      </PillarIcon>
    ),
    title: 'Safe & welcoming',
    body: 'Open to the audience the event says it is.',
  },
  {
    icon: (
      <PillarIcon>
        <path d="M9 11l3 3 8-8" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </PillarIcon>
    ),
    title: 'Honest hosting',
    body: 'Description matches the actual event.',
  },
  {
    icon: (
      <PillarIcon>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </PillarIcon>
    ),
    title: 'Look out for each other',
    body: 'Report what feels off. We handle it.',
  },
];

type RulePair = { do: string; dont: string };

const Rule = ({
  id,
  num,
  title,
  intro,
  pairs,
}: {
  id: string;
  num: number;
  title: string;
  intro?: string;
  pairs: RulePair[];
}): React.JSX.Element => (
  <section id={id} className="privacy-section">
    <h2>
      <span className="privacy-section-num">{String(num).padStart(2, '0')}</span>
      <span>{title}</span>
    </h2>
    {intro && <p>{intro}</p>}
    <div className="rule-grid">
      {pairs.map((p, i) => (
        <div className="rule-pair" key={i}>
          <div className="rule-cell rule-do">
            <span className="rule-mark">✓</span>
            <span>{p.do}</span>
          </div>
          <div className="rule-cell rule-dont">
            <span className="rule-mark">✗</span>
            <span>{p.dont}</span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const CommunityInner = (): React.JSX.Element => {
  const { t } = useLocale();

  useSeo({
    title: 'Community Rules',
    description:
      'Be real, be welcoming, keep events honest. The community guidelines that keep Gather a warm place to meet new people.',
    canonical: '/community',
    jsonLd: breadcrumbJsonLd([
      { name: 'Home', path: '/' },
      { name: 'Community Rules', path: '/community' },
    ]),
  });

  return (
    <div className="landing landing--neon" id="top">
      <Header />
      <main>
        <section className="privacy-page">
          <div className="wrap privacy-wrap">
            <aside className="privacy-toc">
              <div className="privacy-toc-inner">
                <span className="privacy-toc-label">On this page</span>
                <ol>
                  {TOC.map((item, i) => (
                    <li key={item.id}>
                      <a href={`#${item.id}`}>
                        <span className="toc-num">{String(i + 1).padStart(2, '0')}</span>
                        <span className="toc-text">{item.label}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </div>
            </aside>

            <article className="privacy-content">
              <header className="privacy-hero">
                <span className="eyebrow">{t('community.eyebrow')}</span>
                <h1>Community Rules</h1>
                <div className="privacy-meta-row">
                  <span className="privacy-tag">Effective {EFFECTIVE_DATE}</span>
                  <span className="privacy-tag">~ 4 min read</span>
                  <span className="privacy-tag">EN</span>
                </div>
                <p className="privacy-intro">
                  Gather is a place to find real plans with real people. These rules keep that
                  vibe alive. They're written for humans, not lawyers — the legal version lives in
                  the <a href="/terms">Terms</a>.
                </p>
              </header>

              <div className="privacy-pillars">
                {PILLARS.map((p) => (
                  <div className="pillar" key={p.title}>
                    <div className="pillar-icon">{p.icon}</div>
                    <h3>{p.title}</h3>
                    <p>{p.body}</p>
                  </div>
                ))}
              </div>

              <Rule
                id="real"
                num={1}
                title="Be real"
                intro="Use your real name, a real photo, and real intentions. People are deciding whether to spend an evening with you — make it easy for them."
                pairs={[
                  { do: 'Real name and a clear photo of you', dont: 'Stock photos, anime avatars, or someone else\'s face' },
                  { do: 'Honest bio about what you\'re into', dont: 'Catfishing or pretending to be someone you\'re not' },
                  { do: 'One account per person', dont: 'Multiple accounts to skirt bans or boost RSVPs' },
                ]}
              />

              <Rule
                id="welcoming"
                num={2}
                title="Be welcoming"
                intro="Events should be open to the audience they advertise. No discrimination based on race, gender, orientation, religion, disability, or anything similar."
                pairs={[
                  { do: 'Label your event clearly (e.g. "queer-friendly", "beginners welcome")', dont: 'Reject attendees once they show up because of who they are' },
                  { do: 'Make the room feel safe for everyone there', dont: 'Slurs, hateful jokes, or "ironic" hate speech' },
                ]}
              />

              <Rule
                id="safety"
                num={3}
                title="Safety first"
                pairs={[
                  { do: 'Meet in public the first time. Tell a friend where you\'re going.', dont: 'Pressure anyone to share their location or come somewhere private' },
                  { do: 'Trust your gut. Leave if something feels off.', dont: 'Ignore red flags because you don\'t want to be rude' },
                  { do: 'Report behavior that crossed a line', dont: 'Confront a bad actor alone if you don\'t feel safe' },
                ]}
              />

              <Rule
                id="harassment"
                num={4}
                title="No harassment"
                intro="In DMs, in comments, and in person — at the event and after. This is the fastest way to lose your account."
                pairs={[
                  { do: 'Take "no" as an answer the first time', dont: 'Repeated DMs after someone has stopped replying' },
                  { do: 'Compliment people on what they do, not their bodies', dont: 'Sexual comments to strangers, especially uninvited' },
                  { do: 'Walk away from a conflict', dont: 'Threats, doxxing, or piling on someone' },
                ]}
              />

              <Rule
                id="honest"
                num={5}
                title="Honest events"
                intro="What you describe is what people show up to. Surprises that change the deal break trust."
                pairs={[
                  { do: 'Free? Say free. Paid? Say paid.', dont: '"Free networking" that\'s a sales pitch or pyramid scheme' },
                  { do: 'Disclose if you\'re promoting a business', dont: 'Disguising a workshop as a casual hangout' },
                  { do: 'Be specific: where, when, what to bring, dress code', dont: 'Vague details that change after people RSVP' },
                ]}
              />

              <Rule
                id="show-up"
                num={6}
                title="Show up or cancel"
                intro="No-shows hurt the host and the people who showed. If life happens, just cancel — that's fine."
                pairs={[
                  { do: 'Cancel as soon as you know you can\'t make it', dont: 'Ghost on a small intimate event' },
                  { do: 'RSVP only when you actually plan to come', dont: 'Spam-RSVP just to see who\'s going' },
                ]}
              />

              <Rule
                id="privacy"
                num={7}
                title="Privacy of others"
                pairs={[
                  { do: 'Ask before posting photos that show other attendees', dont: 'Post group photos with people who didn\'t agree' },
                  { do: 'Keep what someone shared in DMs in DMs', dont: 'Screenshot and forward private chats' },
                  { do: 'Help people stay anonymous if they want', dont: 'Share someone\'s last name, address, or workplace without consent' },
                ]}
              />

              <Rule
                id="spam"
                num={8}
                title="No spam, no scams"
                pairs={[
                  { do: 'Host clear professional events when you\'re working', dont: 'Cold-DM users trying to sell something' },
                  { do: 'Be upfront about ticketing or affiliate links', dont: 'Crypto pitches, MLM recruiting, "investment opportunities"' },
                  { do: 'Use Gather to bring people together', dont: 'Use Gather to harvest contacts for unrelated marketing' },
                ]}
              />

              <Rule
                id="alcohol"
                num={9}
                title="Drugs & alcohol"
                intro="Local law applies. In Slovakia, alcohol is 18+. Don't post events that involve illegal drugs."
                pairs={[
                  { do: 'Label 18+ events clearly. Check IDs at the door.', dont: 'Welcome minors to an event where alcohol is served' },
                  { do: 'Encourage people who don\'t drink to come anyway', dont: 'Pressure attendees to drink or use anything' },
                  { do: 'Report events that involve illegal drugs', dont: 'Organize, advertise, or sell illegal drugs on Gather' },
                ]}
              />

              <section id="enforcement" className="privacy-section">
                <h2>
                  <span className="privacy-section-num">10</span>
                  <span>How we enforce</span>
                </h2>
                <p>
                  When rules are broken, we usually go gently first. The bigger the harm, the
                  faster we move.
                </p>
                <ol className="enforce-ladder">
                  <li>
                    <span className="enforce-step">Warning</span>
                    <span>We let you know what crossed the line and what to do differently.</span>
                  </li>
                  <li>
                    <span className="enforce-step">Content removal</span>
                    <span>We remove the post, comment, or event that broke the rule.</span>
                  </li>
                  <li>
                    <span className="enforce-step">Temporary suspension</span>
                    <span>You can't post or join events for a set period. Time depends on what happened.</span>
                  </li>
                  <li>
                    <span className="enforce-step">Permanent ban</span>
                    <span>For serious harm, repeat offenses, or anything illegal — your account is gone.</span>
                  </li>
                </ol>
                <p style={{ marginTop: 16 }}>
                  Things that skip straight to a ban: physical harm, sexual harassment, hate
                  speech, doxxing, scams that take money, organized illegal activity, or content
                  involving minors. We may also report serious incidents to the appropriate
                  authorities.
                </p>
                <h3 style={{ marginTop: 24 }}>Appeals</h3>
                <p>
                  Think we got it wrong? Email{' '}
                  <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> with your account and
                  what happened. A real person reviews appeals.
                </p>
              </section>

              <section id="report" className="privacy-section">
                <h2>
                  <span className="privacy-section-num">11</span>
                  <span>Report something</span>
                </h2>
                <p>If you see something that breaks these rules, you have two options:</p>
                <div className="report-grid">
                  <div className="report-card">
                    <div className="report-tag">In the app</div>
                    <p>
                      Tap the ⋯ menu on a post, event, or profile, then choose <strong>Report</strong>.
                      Pick a reason and add context. Reports are confidential.
                    </p>
                  </div>
                  <div className="report-card">
                    <div className="report-tag">By email</div>
                    <p>
                      Write to{' '}
                      <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a> with a description
                      and screenshots if you have them. Useful for things that happened off-platform.
                    </p>
                  </div>
                </div>
                <p style={{ marginTop: 16 }}>
                  If someone is in immediate danger, call your local emergency number first
                  (<strong>112</strong> in Slovakia and the EU), then tell us.
                </p>
              </section>

              <p className="legal-operator">Operator: Maksym Bocharov · Bratislava, Slovakia</p>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export const Community = (): React.JSX.Element => (
  <LocaleProvider>
    <CommunityInner />
  </LocaleProvider>
);
